import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageBanner } from '../components/shared/PageBanner';
import { BlogGrid } from '../components/blog/BlogGrid';
import { BlogFilters } from '../components/blog/BlogFilters';
import { BlogSidebar } from '../components/blog/BlogSidebar';
import { BlogPagination } from '../components/blog/BlogPagination';
import { BlogDisclaimer } from '../components/blog/BlogDisclaimer';
import { useBlogPosts } from '../hooks/useBlogPosts';
import type { BlogFilter } from '../types/blog';

export function Blog() {
  const { category } = useParams<{ category?: string }>();
  const [filters, setFilters] = useState<BlogFilter>({
    sortBy: 'date',
    sortOrder: 'desc'
  });
  const [currentPage, setCurrentPage] = useState(1);

  // Update filters when category parameter changes
  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      category: category
    }));
    setCurrentPage(1); // Reset to first page when category changes
  }, [category]);

  const { data, isLoading, isError, refetch } = useBlogPosts(
    filters, 
    { page: currentPage, limit: 12 }
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageBanner 
        title="Nigerian Forex Market Blog"
        description="Latest insights on live Naira exchange rate today, Dollar to Naira black market rate analysis, and official exchange rate Nigeria updates"
        backgroundImage="https://images.unsplash.com/photo-1554224155-6726b3ff858f"
      />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <BlogDisclaimer />
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <BlogFilters filters={filters} onFiltersChange={setFilters} />
            
            <BlogGrid 
              posts={data?.posts || []}
              isLoading={isLoading}
              isError={isError}
              onRetry={refetch}
            />

            {data?.pagination && data.pagination.totalPages > 1 && (
              <div className="mt-12">
                <BlogPagination
                  pagination={data.pagination}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <BlogSidebar />
          </div>
        </div>
      </div>
    </div>
  );
}